import { graphql, PageProps } from 'gatsby'

import React, { ReactElement, useState } from 'react'
import { HeroBannerContentfulModel, HeroBannerSectionContentfulModel } from 'src/api/models/HeroBanner'
import OverlayVideo from 'src/elements/video/overlay.video'
import { ButtonContentfulModel } from '../api/models/Button'
import { CreatorSliderContentfulModel, CreatorSliderSectionContentfulModel } from '../api/models/CreatorsSlider'
import { FeatureListContentfulModel, FeatureListSectionContentfulModel } from '../api/models/FeatureList'
import { HeadingContentfulModel } from '../api/models/Heading'
import { PhoneCarouselContentfulModel, PhoneCarouselSectionContentfulModel } from '../api/models/PhoneCarousel'
import { TrustedByContentfulModel, TrustedBySectionContentfulModel } from '../api/models/TrustedBy'
import Layout from '../elements/layout/layout'
import CallForAction from '../molecules/callToAction/callToAction'
import { FeatureListEntryProps } from '../molecules/featureListEntry/featureList.entry'
import TrustedBy from '../molecules/trustedBy/trustedBy.landing'
import BannerLanding from '../organisms/banners/Banner.landing'
import PhoneCarousel from '../organisms/carousels/PhoneCarousel.landing'
import Creators from '../organisms/creators/creators'
import FeatureList from '../organisms/featureList/featureList.list'
import HomePageVideo from 'src/videos/2021-homepage-video-optimized-2-1.mp4';
import ExperimentPageContainer from 'src/molecules/experiments/ExperimentPageContainer';
import { HomepageCTASectionContentfulModel } from 'src/api/models/Homepage'

export interface PageData {
  contentfulPage: {
    title: string;
    seoTitle: string;
    seoDescription?: string;
    seoImage?: {
      file?: {
        url: string;
      };
    };
    contentfulchildren: (
      | HeroBannerSectionContentfulModel
      | PhoneCarouselSectionContentfulModel
      | FeatureListSectionContentfulModel
      | TrustedBySectionContentfulModel
      | CreatorSliderSectionContentfulModel
      | HomepageCTASectionContentfulModel
    )[];
  };
}

interface LandingProps extends PageProps {
  data: PageData
}

export const BuilderPageRenderer = (props: LandingProps): ReactElement => {
  const [ videoModalIsOpened, setVideoModalIsOpened ] = useState(false);

  const helmetData = {
    title: props.data.contentfulPage.seoTitle,
    description: props.data.contentfulPage.seoDescription,
    previewImageURL: props.data.contentfulPage.seoImage?.file?.url
  }

  // >> Banner sections
  const bannerSectionData = (props.data.contentfulPage.contentfulchildren as HeroBannerSectionContentfulModel[])
    .find(data => data.label.includes('Hero Banner Container')).contentfulchildren
  const bannerData: HeroBannerContentfulModel = bannerSectionData
    .find(data => data.internal?.type === 'ContentfulHeroBanner');

  // >> Phone Carousel sections
  const phoneCarouselSectionData = (props.data.contentfulPage.contentfulchildren as PhoneCarouselSectionContentfulModel[])
    .find(data => data.label.includes('Phone Carousel Container')).contentfulchildren
  const phoneCarouselHeaderText = (phoneCarouselSectionData
    .find(data => data.internal?.type === 'ContentfulHeading') as HeadingContentfulModel)?.text;
  const phoneCarouselButtonProps = (phoneCarouselSectionData
    .filter(data => data.internal?.type === 'ContentfulButton') as ButtonContentfulModel[]);
  const phoneCarouselProps = (phoneCarouselSectionData
    .find(data => data.internal?.type === 'ContentfulPhoneCarousel') as PhoneCarouselContentfulModel).contentfulchildren;
  // ---------------------------

  // >> Sided Features Container
  const featureListSectionData = (props.data.contentfulPage.contentfulchildren as FeatureListSectionContentfulModel[])
    .find(data => data.label === 'Sided Features Container').contentfulchildren
  const featureListData: FeatureListEntryProps[] = (featureListSectionData
    .find(data => data.internal?.type === 'ContentfulFeatures') as FeatureListContentfulModel)?.contentfulchildren;
  const featureListButtonProps = (featureListSectionData
    .filter(data => data.internal?.type === 'ContentfulButton') as ButtonContentfulModel[]);
  // ---------------------------

  // >> Trusted By Container
  const trustedBySectionData = (props.data.contentfulPage.contentfulchildren as TrustedBySectionContentfulModel[])
    .find(data => data.label === 'Trusted By Container').contentfulchildren
  const trustedByLogoData = (trustedBySectionData
    .find(data => data.internal?.type === 'ContentfulTrustedClients') as TrustedByContentfulModel)?.contentfulchildren;
  const trustedByHeader = (trustedBySectionData
    .find(data => data.internal?.type === 'ContentfulHeading') as HeadingContentfulModel);
  // ---------------------------

  // >> Creators Container
  const creatorsSectionData = (props.data.contentfulPage.contentfulchildren as CreatorSliderSectionContentfulModel[])
    .find(data => data.label === 'Stories Container').contentfulchildren
  const creatorsImageData = (creatorsSectionData
    .find(data => data.internal?.type === 'ContentfulSlider') as CreatorSliderContentfulModel)?.contentfulchildren;
  const creatorsHeader = (creatorsSectionData
    .find(data => data.internal?.type === 'ContentfulHeading') as HeadingContentfulModel);
  // ---------------------------

  // >> CTA Container
  const ctaSectionData = (props.data.contentfulPage.contentfulchildren as HomepageCTASectionContentfulModel[])
    .find(data => data.label === 'CTA Container').contentfulchildren
  const ctaSectionTitle = ctaSectionData.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const ctaSectionButton = ctaSectionData.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;
  // ---------------------------

  const videoButton = bannerData.buttons.find(button => !!button.video)

  return (
    <Layout helmet={helmetData} currentPageId="382wfkjgH0xjMZ2AV69d7X" location={props.location}>
      <BannerLanding
        {...{
          title: bannerData.title,
          description: bannerData.description,
          textPosition: 'right',
          actionLink: {
            url: bannerData.buttons[ 0 ].url,
            text: bannerData.buttons[ 0 ].text,
            caption: bannerData.buttons[ 0 ].helpText || null
          },
          actionButton: {
            text: bannerData.buttons[ 1 ].text,
            onClick: () => setVideoModalIsOpened(true)
          },
          videoUrl: HomePageVideo,
        }}
      />
      {
        videoButton && (
          <OverlayVideo
            overlayVisible={videoModalIsOpened}
            setOverlayVisible={setVideoModalIsOpened}
            src={videoButton.video.file.url}
          />
        )
      }
      <PhoneCarousel actionButtons={phoneCarouselButtonProps} phoneItems={phoneCarouselProps} headerText={phoneCarouselHeaderText} />
      <TrustedBy heading={trustedByHeader} logos={trustedByLogoData} />
      <FeatureList features={featureListData} actionButtons={featureListButtonProps} />
      <Creators
        title={creatorsHeader.text}
        creators={creatorsImageData}
      />
      <CallForAction
        title={ctaSectionTitle.text}
        actionButton={ctaSectionButton}
      />
    </Layout>
  )
}

const MembershipPage = (props: LandingProps): ReactElement => (
  process.env.GATSBY_OPTIMIZELY === 'off' ? <BuilderPageRenderer {...props} /> : <ExperimentPageContainer  {...props} />
)

// TODO: Arrange graphql into graph blocks and exports under src/api folder
export const pageQuery = graphql`
  query WebsiteBuilderPageQuery {
    contentfulPage(contentful_id: {eq: "382wfkjgH0xjMZ2AV69d7X"}) {
      title
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      contentfulchildren {
        id
        label
        contentfulchildren {
          ...HeroBannerContent
          ...HeadingContent
          ...ButtonContent
          ...TrustedClientsContent
          ...PhoneCarouselContent
          ...FeaturesContent
          ...SliderContent
        }
      }
    }
  }
`
export default MembershipPage;
